import React from 'react';
import { Box, Card, Skeleton, Stack, Typography } from '@mui/material';
import { formatMoney } from '../utils/constant';
import { useIsMobile } from '../hooks/use-is-mobile';

function MobileOrdersCostSummary({ productCost, shippingCost, totalCost, isLoading }) {
  const renderAmount = (value) => (isLoading
    ? <Skeleton width={90} height={24} sx={{ bgcolor: 'rgba(255,255,255,0.25)' }} />
    : formatMoney(value || 0));

  return (
    <Card
      elevation={0}
      sx={{
        p: 2,
        borderRadius: 4,
        border: 'none',
        color: 'primary.contrastText',
        background: (theme) =>
          `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
      }}
    >
      <Typography variant="caption" sx={{ opacity: 0.85 }}>
        Total Cost
      </Typography>
      <Typography variant="h5" sx={{ fontWeight: 800, lineHeight: 1.3 }}>
        {renderAmount(totalCost)}
      </Typography>
      <Stack direction="row" spacing={2} sx={{ mt: 1.5, pt: 1.5, borderTop: '1px solid rgba(255,255,255,0.2)' }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="caption" sx={{ opacity: 0.85 }}>Products</Typography>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{renderAmount(productCost)}</Typography>
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="caption" sx={{ opacity: 0.85 }}>Shipping</Typography>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{renderAmount(shippingCost)}</Typography>
        </Box>
      </Stack>
    </Card>
  );
}

export function OrdersCostSummary({
  productCost = 0,
  shippingCost = 0,
  isLoading = false,
}) {
  const isMobile = useIsMobile();
  const totalCost = (productCost || 0) + (shippingCost || 0);

  if (isMobile) {
    return (
      <MobileOrdersCostSummary
        productCost={productCost}
        shippingCost={shippingCost}
        totalCost={totalCost}
        isLoading={isLoading}
      />
    );
  }

  return (
    <Card
      variant="outlined"
      sx={{
        p: 2,
        borderRadius: 2.5,
        bgcolor: 'background.paper',
      }}
    >
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        useFlexGap
        flexWrap="wrap"
      >
        <Box sx={{ minWidth: 200 }}>
          <Typography variant="caption" color="text.secondary">
            Total Product Cost
          </Typography>
          {isLoading ? (
            <Skeleton width={120} height={32} />
          ) : (
            <Typography variant="h6">
              {formatMoney(productCost || 0)}
            </Typography>
          )}
        </Box>
        <Box sx={{ minWidth: 200 }}>
          <Typography variant="caption" color="text.secondary">
            Total Shipping Cost
          </Typography>
          {isLoading ? (
            <Skeleton width={120} height={32} />
          ) : (
            <Typography variant="h6">
              {formatMoney(shippingCost || 0)}
            </Typography>
          )}
        </Box>
        <Box sx={{ minWidth: 200, ml: { sm: 'auto' } }}>
          <Typography variant="caption" color="text.secondary">
            Total Cost
          </Typography>
          {isLoading ? (
            <Skeleton width={120} height={32} />
          ) : (
            <Typography variant="h6" color="primary">
              {formatMoney(totalCost)}
            </Typography>
          )}
        </Box>
      </Stack>
    </Card>
  );
}
